import type React from 'react';

import {
  AdornedInput,
  Adornment,
  ButtonGroup,
  FormField,
  Label,
  Paragraph,
  Textarea,
  ToggleButton,
} from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import { STORAGE } from '../../../../shared/constants/index';
import type { Section } from '../../../../shared/models/index';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function Storage({ section, setSectionValues }: Props): JSX.Element {
  const isSpaceNone = section.getFieldValue(STORAGE.FIELD_NAME.SPACE_NONE);
  const isSpaceIncluded = section.getFieldValue(STORAGE.FIELD_NAME.SPACE_INCLUDED);
  const isSpaceAdditionalFee = section.getFieldValue(STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE);

  function onDescriptionChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [STORAGE.FIELD_NAME.SPACE_PROVIDED_DESCRIPTION]: value });
  }

  function onFeeChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE_AMOUNT]: value });
  }

  function onNoneClick(): void {
    setSectionValues({
      [STORAGE.FIELD_NAME.SPACE_NONE]: true,
      [STORAGE.FIELD_NAME.SPACE_PROVIDED]: false,
      [STORAGE.FIELD_NAME.SPACE_INCLUDED]: false,
      [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE]: false,
    });
  }

  function onIncludedClick(): void {
    setSectionValues({
      [STORAGE.FIELD_NAME.SPACE_NONE]: false,
      [STORAGE.FIELD_NAME.SPACE_PROVIDED]: true,
      [STORAGE.FIELD_NAME.SPACE_INCLUDED]: true,
      [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE]: false,
    });
  }

  function onAdditionalClick(): void {
    setSectionValues({
      [STORAGE.FIELD_NAME.SPACE_NONE]: false,
      [STORAGE.FIELD_NAME.SPACE_PROVIDED]: true,
      [STORAGE.FIELD_NAME.SPACE_INCLUDED]: false,
      [STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE]: true,
    });
  }

  return (
    <>
      <Paragraph marginBottom="xs" fontType="h6">
        Is extra storage space available?
      </Paragraph>
      <ButtonGroup fluid direction="column" conjoined marginBottom="sm" aria-label="stoage available">
        <ToggleButton onClick={onNoneClick} pressed={isSpaceNone}>
          No, none is available
        </ToggleButton>
        <ToggleButton onClick={onIncludedClick} pressed={isSpaceIncluded}>
          Yes, included in rent
        </ToggleButton>
        <ToggleButton onClick={onAdditionalClick} pressed={isSpaceAdditionalFee}>
          Yes, for an additional fee
        </ToggleButton>
      </ButtonGroup>
      {(isSpaceIncluded || isSpaceAdditionalFee) && (
        <FormField marginBottom="sm">
          <Label id="area-and-location-description">Describe the area and its location</Label>
          <Textarea
            aria-labelledby="area-and-location-description"
            resize="vertical"
            placeholder="(example: Locker #10 in the basement.)"
            value={section.getFieldValue(STORAGE.FIELD_NAME.SPACE_PROVIDED_DESCRIPTION)}
            onChange={onDescriptionChange}
          />
        </FormField>
      )}
      {isSpaceAdditionalFee && (
        <FormField
          label={<Label id="storage-fee">Storage fee</Label>}
          control={
            <AdornedInput
              inputId="AdornedInput-storage-fee"
              leftAdornment={<Adornment>$</Adornment>}
              rightAdornment={<Adornment>per month</Adornment>}
              input={
                <CurrencyInput
                  aria-labelledby="storage-fee"
                  data-test="storage-fee"
                  placeholder={CURRENCY_INPUT_PLACEHOLDER}
                  value={section.getFieldValue(STORAGE.FIELD_NAME.SPACE_ADDITIONAL_FEE_AMOUNT)}
                  onChange={onFeeChange}
                  onBlur={onFeeChange}
                />
              }
            />
          }
        />
      )}
    </>
  );
}
